/**
 * Top Contracts — row shape and the figures derived from it.
 *
 * The panels rank a symbol's contracts by **directional net premium**, calls
 * and puts side by side. Everything here is pure: the wire mapping lives in
 * `services/topContractsService.ts`, and the rendering in `TopContractsPanel`.
 *
 * Contract settled with QuantForge 2026-09-01 — see
 * `docs/plans/2026-09-01-top-contracts-net-premium-contract.md` for the four
 * traps it records. Two of them are load-bearing here and are repeated at the
 * point of use below.
 */

export type OptionType = 'Call' | 'Put';

/** One contract on the board. Field names are ours; the wire is `snake_case`. */
export interface TopContractRow {
  strike: number;
  optionType: OptionType;
  /** `YYYY-MM-DD`. */
  expiry: string;

  /**
   * Signed dollars. Positive is net bullish, negative net bearish.
   *
   * ⚠️ **Directional, and QF computes it — never derive it here.** The formula
   * is `(call_ask + put_bid) − (call_bid + put_ask)`; the lookalike
   * `(call_ask + put_ask) − (call_bid + put_bid)` is AGGRESSION and scores
   * bearish put buying as positive. The two agree in sign often enough that a
   * mixup survives review. Verified against live tape on 2026-09-01: a 1640P
   * with 1.39% at the offer (i.e. puts SOLD, bullish) carries a correctly
   * POSITIVE net premium here, where the lookalike returns the negation.
   */
  netPremium: number;

  /**
   * Share of DIRECTIONAL premium lifted at the offer, 0–100.
   *
   * Denominator is ask+bid only — mid and no-side are excluded, because the
   * question is which way the directional money leaned.
   */
  askPct: number;

  /**
   * Share of TOTAL premium that never picked a side, 0–100.
   *
   * ⚠️ **This does NOT sum to 100 with `askPct`, by design.** Different
   * denominators: `askPct` divides by ask+bid, this divides by
   * ask+bid+mid+no_side. Two adjacent percentages that fail to sum to 100 is
   * exactly the thing a later reader "fixes" — do not.
   *
   * Its purpose is honesty about confidence: a contract with 80% of its money
   * at mid can show a decisive-looking `askPct` resting on two prints.
   */
  midPct: number;

  volume: number;
  openInterest: number;
}

/** A symbol's board: both sides, plus what the moneyness is measured against. */
export interface TopContractsBoard {
  /** Ranked by signed net premium, descending. */
  calls: TopContractRow[];
  puts: TopContractRow[];

  /**
   * Underlying price the moneyness column is measured against.
   *
   * ⚠️ **A LIVE TICK, not the session's close.** See `spotAsOf`, which the
   * panel does not currently surface.
   */
  spot: number | null;

  /**
   * When `spot` was taken, ISO-8601 UTC.
   *
   * ⚠️ **The gap between this and `sessionDate` is NOT bounded by one overnight
   * move.** A long weekend, a market holiday, or a halted name widens it to
   * several days. Never write caption logic against a 24-hour assumption —
   * read the actual gap off this field.
   */
  spotAsOf: string | null;

  /** `YYYY-MM-DD` — the session the rankings cover. */
  sessionDate: string;
}

/* ── derived figures ────────────────────────────────────────────────── */

/**
 * Calendar days from `fromIso` to `expiryIso`.
 *
 * ⚠️ **Calendar days, not sessions** — "141d" on a contract means what a chain
 * screen means by it. Deliberately different from the session counting used for
 * bar windows elsewhere in the Explorer.
 *
 * Both sides parse as UTC midnight so a DST boundary between them cannot make
 * the difference 140.5 and floor it a day short.
 */
export function daysToExpiry(expiryIso: string, fromIso: string): number | null {
  const expiry = Date.parse(`${expiryIso}T00:00:00Z`);
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  if (!Number.isFinite(expiry) || !Number.isFinite(from)) return null;
  return Math.round((expiry - from) / 86_400_000);
}

export interface Moneyness {
  /** Distance from spot as a percentage OF SPOT. Always positive. */
  pct: number;
  /** True when the contract has intrinsic value at `spot`. */
  itm: boolean;
}

/**
 * How far the strike sits from spot, and which side of it.
 *
 * ⚠️ **In/out of the money INVERTS between calls and puts.** A call is in the
 * money below spot, a put above it. Getting this backwards mislabels every row
 * on one of the two panels while the percentage stays right — an error that
 * survives a glance.
 *
 * The percentage is unsigned: the ITM/OTM label already carries direction, and
 * a signed percentage beside it reads as a gain or loss rather than a distance.
 */
export function moneyness(
  strike: number,
  spot: number,
  optionType: OptionType
): Moneyness | null {
  if (!Number.isFinite(strike) || !Number.isFinite(spot) || spot <= 0) return null;
  return {
    pct: (Math.abs(spot - strike) / spot) * 100,
    itm: optionType === 'Call' ? strike < spot : strike > spot,
  };
}

/**
 * Contract label — the strike alone.
 *
 * No C/P suffix: each panel is one side, named in its heading, so a suffix
 * repeated down every row states what the column already guarantees.
 *
 * Two decimals always, so `227.50` and `230.00` align down the column instead
 * of jittering between `227.5` and `230`.
 */
export function contractLabel(row: TopContractRow): string {
  return row.strike.toFixed(2);
}

/**
 * Signed money, one decimal — `+$815.0M`.
 *
 * Deliberately not `fmtCompactNum`: that drops trailing zeros, so a round
 * figure renders `815M` beside a neighbour's `777.3M` and the decimal points
 * stop lining up down the column.
 *
 * The minus is U+2212, not a hyphen, so it matches the digit width of the plus
 * and the column does not shift by a pixel between signs.
 */
export function fmtNetPremium(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';

  const sign = v >= 0 ? '+' : '−';
  const abs = Math.abs(v);

  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(abs)}`;
}

/**
 * Each row's share of the largest net premium on ITS OWN side, 0–100.
 *
 * ⚠️ **Scaled within the panel, never across the pair.** The top call and the
 * top put are both full-width bars whatever their premiums, so bar length
 * compares contracts inside one panel and says nothing about the other. Sharing
 * one scale would flatten the quieter side into a row of stubs.
 *
 * Magnitudes are used, so a net-sold row still draws a bar; the sign lives in
 * the number beside it.
 */
export function premiumBarPct(rows: TopContractRow[], row: TopContractRow): number {
  const top = Math.max(...rows.map((r) => Math.abs(r.netPremium)), 0);
  if (!Number.isFinite(top) || top <= 0) return 0;
  return Math.min(100, (Math.abs(row.netPremium) / top) * 100);
}

/**
 * At or above this share bought, a row is marked as one-sided.
 *
 * Four in five contracts lifted at the offer is where the flow stops looking
 * two-way. Below it the bar still renders, in neutral — the emphasis is
 * conditional, not the data.
 */
export const ASK_EMPHASIS_PCT = 80;
